import { GLTexture } from '../GL/texture.js';
import { GLMesh } from '../GL/scene/mesh.js';
import { Display } from '../GL/programs/display.js';
import { Blit } from '../GL/programs/blit.js';
import { LuminosityContrastProgram } from '../GL/programs/operations/filters/luminosityContrast.js';
import { HueSaturationProgram } from '../GL/programs/operations/filters/hueSaturation.js';
import { ColorBalanceProgram } from '../GL/programs/operations/filters/colorBalance.js';
import { LevelsProgram } from '../GL/programs/operations/filters/levels.js';
import { SharpenProgram } from '../GL/programs/operations/filters/sharpen.js';
import { AlphaSelectionProgram } from '../GL/programs/operations/filters/alphaSelection.js';
import { SelectionProgram } from '../GL/programs/selection.js';
import { BrushStrokeProgram } from '../GL/programs/stroke/brush.js';
import { StampStrokeProgram } from '../GL/programs/stroke/stamp.js';
import { BrushProgram } from '../GL/programs/operations/tools/brush.js';
import { DensityProgram } from '../GL/programs/operations/tools/density.js';
import { StampProgram } from '../GL/programs/operations/tools/stamp.js';
import { DrawBrushProgram } from '../GL/programs/draw/brush.js';
import { DrawSelectionProgram } from '../GL/programs/draw/selection.js';
import * as glMatrix from 'gl-matrix';
import { Alpha2ColorProgram } from './programs/operations/filters/alpha2color.js';
import { SelectiveCorrectionProgram } from './programs/operations/filters/selectiveCorrection.js';
export class GLEngineResources {
    _gl: WebGL2RenderingContext;
    _programs: {};
    _meshes: {};
    _identity: glMatrix.mat4;
    _projection: glMatrix.mat4;
    _lookat: glMatrix.mat4;
    _target: GLTexture;
    _framebuffer: WebGLFramebuffer;
    _drawtarget: GLTexture;
    _drawbuffer: any;
    _width: number;
    _height: number;
    _scaledWidth: any;
    _scaledHeight: number;

    constructor(templates, gl) {
        var self = this;
        self._gl = gl;

        self._programs = {};
        self._programs['display'] = Display.create(templates, gl);
        self._programs['blit'] = Blit.create(templates, gl);
        self._programs['luminosityContrast'] = LuminosityContrastProgram.create(templates, gl);
        self._programs['hueSaturation'] = HueSaturationProgram.create(templates, gl);
        self._programs['colorBalance'] = ColorBalanceProgram.create(templates, gl);
        self._programs['levels'] = LevelsProgram.create(templates, gl);
        self._programs['sharpen'] = SharpenProgram.create(templates, gl);
        self._programs['alpha2Color'] = Alpha2ColorProgram.create(templates, gl);
        self._programs['selectiveCorrection'] = SelectiveCorrectionProgram.create(templates, gl);
        self._programs['alphaSelection'] = AlphaSelectionProgram.create(templates, gl);
        self._programs['selection'] = SelectionProgram.create(templates, gl);
        self._programs['brushStroke'] = BrushStrokeProgram.create(templates, gl);
        self._programs['stampStroke'] = StampStrokeProgram.create(templates, gl);
        self._programs['brush'] = BrushProgram.create(templates, gl);
        self._programs['density'] = DensityProgram.create(templates, gl);
        self._programs['stamp'] = StampProgram.create(templates, gl);
        self._programs['draw_brush'] = DrawBrushProgram.create(templates, gl);
        self._programs['draw_selection'] = DrawSelectionProgram.create(templates, gl);

        self._meshes = {};
        self._meshes['image'] = GLMesh.createRenderQuad(gl);
        self._meshes['layer'] = GLMesh.createRenderQuad(gl);
        self._meshes['unit'] = GLMesh.createRenderQuad(gl);
        self._meshes['stoke'] = GLMesh.createRenderQuad(gl);

        self._identity = glMatrix.mat4.create();

        self._projection = glMatrix.mat4.create();
        glMatrix.mat4.perspective(self._projection, 90 * Math.PI / 180, 1, 0.01, 1000);
        self._lookat = glMatrix.mat4.create();
        glMatrix.mat4.lookAt(self._lookat, glMatrix.vec3.fromValues(0, 0, 1), glMatrix.vec3.fromValues(0, 0, 0), glMatrix.vec3.fromValues(0, 1, 0));

        self._target = GLTexture.createTexture2d(gl, false);
        self._framebuffer = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, self._framebuffer);
        self._target.bind();
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, self._target._texture, 0);
        self._target.unbind();
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

        self._drawtarget = GLTexture.createTexture2d(gl, false);
        self._drawbuffer = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, self._drawbuffer);
        self._drawtarget.bind();
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, self._drawtarget._texture, 0);
        self._drawtarget.unbind();
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

        self._width = 0; self._height = 0;
    }

    get programs() {
        return this._programs;
    }

    get meshes() {
        return this._meshes;
    }

    get gl() {
        return this._gl;
    }

    get framebuffer() {
        return this._framebuffer;
    }

    get drawbuffer() {
        return this._drawbuffer;
    }

    get drawtarget() {
        return this._drawtarget;
    }

    get width() {
        return this._width;
    }

    get height() {
        return this._height;
    }

    get aspect() {
        return this._width / this._height;
    }

    get projection() {
        return this._projection;
    }

    get lookAt() {
        return this._lookat;
    }

    get identity() {
        return this._identity;
    }

    get viewportScale() {
        var self = this;
        var gl = self.gl;
        var canvasAspect = gl.canvas.width / gl.canvas.height;
        if (canvasAspect < self.aspect) {
            self._scaledWidth = gl.canvas.width;
            self._scaledHeight = self._scaledWidth / self.aspect;
        } else {
            self._scaledHeight = gl.canvas.height;
            self._scaledWidth = self._scaledHeight * self.aspect;
        }

        return glMatrix.vec3.fromValues(self._scaledWidth / gl.canvas.width, self._scaledHeight / gl.canvas.height, 1);
    }

    setSize(width, height) {
        var self = this;
        var gl = self._gl;
        self._width = width;
        self._height = height;
        self._target.bind();
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        self._target.unbind();

        self._drawtarget.bind();
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        self._drawtarget.unbind();

        glMatrix.mat4.perspective(self._projection, 90 * Math.PI / 180, self.aspect, 0.01, 1000);
    }

    destroy() {
        var self = this;
        var gl = self._gl;
        for (var item in self._meshes) {
            self._meshes[item].destroy();
        }

        for (var item in self._programs) {
            self._programs[item].destroy();
        }

        self._target.destroy();
        gl.deleteFramebuffer(self._framebuffer);
        self._drawtarget.destroy();
        gl.deleteFramebuffer(self._drawbuffer);
    }

}